import * as Sentry from "@sentry/nextjs";

/**
 * Утилиты для работы с Sentry мониторингом
 */

/**
 * Захватывает ошибку и отправляет в Sentry
 * @param error - Ошибка для отправки
 * @param context - Дополнительный контекст
 */
export const captureError = (error: Error, context?: Record<string, unknown>) => {
  Sentry.captureException(error, {
    extra: context,
    tags: {
      component: "aseed-client",
    },
  });
};

/**
 * Захватывает сообщение и отправляет в Sentry
 * @param message - Сообщение для отправки
 * @param level - Уровень сообщения
 * @param context - Дополнительный контекст
 */
export const captureMessage = (
  message: string,
  level: "info" | "warning" | "error" = "info",
  context?: Record<string, unknown>
) => {
  Sentry.captureMessage(message, {
    level,
    extra: context,
    tags: {
      component: "aseed-client",
    },
  });
};

/**
 * Добавляет хлебные крошки для отслеживания пользовательских действий
 * @param message - Сообщение
 * @param category - Категория действия
 * @param data - Дополнительные данные
 */
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
};

/**
 * Устанавливает пользовательский контекст
 * @param user - Данные пользователя
 */
export const setUser = (user: {
  id?: string;
  email?: string;
  username?: string;
}) => {
  Sentry.setUser(user);
};

/**
 * Устанавливает теги для группировки ошибок
 * @param tags - Объект с тегами
 */
export const setTags = (tags: Record<string, string>) => {
  Sentry.setTags(tags);
};

/**
 * Устанавливает дополнительный контекст
 * @param context - Объект с контекстом
 */
export const setContext = (key: string, context: Record<string, unknown>) => {
  Sentry.setContext(key, context);
};

/**
 * Обертка для асинхронных функций с автоматическим захватом ошибок
 * @param fn - Асинхронная функция
 * @param context - Контекст для ошибки
 */
export const withSentryErrorCapture = async <T>(
  fn: () => Promise<T>,
  context?: string
): Promise<T | null> => {
  try {
    return await fn();
  } catch (error) {
    captureError(error as Error, { context });
    return null;
  }
};

/**
 * Обертка для синхронных функций с автоматическим захватом ошибок
 * @param fn - Синхронная функция
 * @param context - Контекст для ошибки
 */
export const withSentryErrorCaptureSync = <T>(
  fn: () => T,
  context?: string
): T | null => {
  try {
    return fn();
  } catch (error) {
    captureError(error as Error, { context });
    return null;
  }
};
